import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');

    const form = e.currentTarget;
    const formData = new FormData(form);
    
    // Validar textarea
    const message = formData.get('message') as string;
    if (message && message.length > 500) {
      setFormStatus('error');
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d5hco2s52fie682ghe4g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });

      if (response.ok) {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 5000);
      } else {
        setFormStatus('error');
      }
    } catch (error) {
      setFormStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F5F2]">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white/95 backdrop-blur-sm shadow-md' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-20">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center space-x-3">
              <img 
                src="https://static.readdy.ai/image/1a62d479ea6bd5856073579187b5ee28/40c1fc3e267b957eadf5ce9a3c5ee7f2.png" 
                alt="Emprendedor de Dios Logo" 
                className="h-12 w-auto"
              />
              <span className={`font-serif text-xl font-bold ${isScrolled ? 'text-[#0F0F0F]' : 'text-white'}`}>
                EMPRENDEDOR DE DIOS
              </span>
            </div>
            <div className="hidden md:flex items-center space-x-8">
              <button onClick={() => scrollToSection('sobre')} className={`text-sm font-medium transition-colors hover:text-[#C9A24D] ${isScrolled ? 'text-[#6B4A2B]' : 'text-white'}`}>
                Sobre el Libro
              </button>
              <button onClick={() => scrollToSection('formatos')} className={`text-sm font-medium transition-colors hover:text-[#C9A24D] ${isScrolled ? 'text-[#6B4A2B]' : 'text-white'}`}>
                Formatos
              </button>
              <button onClick={() => scrollToSection('webinar')} className={`text-sm font-medium transition-colors hover:text-[#C9A24D] ${isScrolled ? 'text-[#6B4A2B]' : 'text-white'}`}>
                Webinar
              </button>
              <button onClick={() => scrollToSection('comunidad')} className={`text-sm font-medium transition-colors hover:text-[#C9A24D] ${isScrolled ? 'text-[#6B4A2B]' : 'text-white'}`}>
                Comunidad
              </button>
              <button 
                onClick={() => scrollToSection('formatos')}
                className="bg-[#C9A24D] text-[#0F0F0F] px-6 py-2.5 rounded-lg font-medium text-sm hover:bg-[#B89240] transition-all hover:scale-105 whitespace-nowrap"
              >
                Comprar Ahora
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://static.readdy.ai/image/1a62d479ea6bd5856073579187b5ee28/ad5ae1db6bcbf851465aa2c3486468c0.png"
            alt="Taller de Carpintería"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50"></div>
        </div>
        
        <div className="relative z-10 text-center px-6 max-w-5xl mx-auto">
          <div className="inline-block bg-[#C9A24D]/20 backdrop-blur-sm border border-[#C9A24D]/40 px-6 py-2 rounded-full mb-8">
            <span className="text-[#C9A24D] text-xs font-semibold tracking-widest uppercase">Negocios con Propósito Eterno</span>
          </div>
          
          <h1 className="font-serif text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
            EMPRENDEDOR DE DIOS
          </h1>
          
          <p className="font-light text-[#C9A24D] text-2xl lg:text-3xl mb-8">
            Negocios con Fe y Propósito
          </p>
          
          <p className="font-serif italic text-white/90 text-lg lg:text-xl mb-6 max-w-3xl mx-auto leading-relaxed">
            Consejos eternos para emprender hoy, inspirados en Jesús,<br />
            el mayor y más grande emprendedor de todos los tiempos.
          </p>
          
          <p className="text-white/85 text-base lg:text-lg mb-4 max-w-2xl mx-auto leading-relaxed">
            Jesús no habló de negocios desde una oficina.<br />
            Habló desde campos, redes, semillas, talentos, cosechas y mayordomía.<br />
            Este libro traduce esos principios eternos al mundo real del emprendimiento moderno.
          </p>
          
          <p className="text-[#C9A24D] text-lg font-medium mb-10">
            Dos formatos. Un mismo propósito.<br />
            Ordenar tu negocio, madurar tu fe y construir con visión eterna.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-5">
            <button 
              onClick={() => scrollToSection('formatos')}
              className="bg-[#C9A24D] text-[#0F0F0F] px-10 py-4 rounded-lg font-semibold text-base hover:bg-[#B89240] transition-all hover:scale-105 shadow-xl flex items-center space-x-3 whitespace-nowrap w-full sm:w-auto justify-center"
            >
              <i className="ri-book-2-line text-xl"></i>
              <span>COMPRAR LIBRO FÍSICO + CUADERNO</span>
            </button>
            <button 
              onClick={() => scrollToSection('formatos')}
              className="border-2 border-[#C9A24D] bg-transparent text-[#C9A24D] px-10 py-4 rounded-lg font-semibold text-base hover:bg-[#C9A24D]/10 transition-all hover:scale-105 flex items-center space-x-3 whitespace-nowrap w-full sm:w-auto justify-center"
            >
              <i className="ri-download-cloud-line text-xl"></i>
              <span>EBOOK + CUADERNO DIGITAL</span>
            </button>
          </div>
        </div>
        
        <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce">
          <i className="ri-arrow-down-line text-white text-3xl"></i>
        </div>
      </section>

      {/* Sobre el Libro Section */}
      <section id="sobre" className="py-24 lg:py-32 bg-[#F7F5F2] relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%236B4A2B" fill-opacity="1"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'
        }}></div>
        
        <div className="max-w-7xl mx-auto px-6 lg:px-20">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <div className="relative rounded-xl overflow-hidden shadow-2xl">
                <img 
                  src="https://static.readdy.ai/image/1a62d479ea6bd5856073579187b5ee28/e98f78c9ceba65504991da0ab148f896.png"
                  alt="Libro Emprendedor de Dios"
                  className="w-full h-auto"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-[#C9A24D]/10 rounded-full blur-3xl"></div>
            </div>
            
            <div>
              <div className="flex items-center space-x-4 mb-6">
                <div className="w-12 h-0.5 bg-[#C9A24D]"></div>
                <span className="text-[#6B4A2B] text-sm font-bold tracking-widest uppercase">¿De Qué Trata?</span>
              </div>
              
              <h2 className="font-serif text-4xl lg:text-5xl font-bold text-[#0F0F0F] mb-6 leading-tight">
                Emprender con fe<br />
                no es improvisar.<br />
                Soñar en grande<br />
                no es ignorar la realidad.
              </h2>
              
              <p className="font-serif italic text-[#6B4A2B]/80 text-lg lg:text-xl mb-8 leading-relaxed">
                Emprendedor de Dios es una guía clara, pastoral y práctica para cristianos que aman a Dios y desean emprender con orden, carácter, ética y propósito.
              </p>
              
              <p className="text-[#0F0F0F]/70 text-base mb-8 leading-relaxed">
                Aquí no encontrarás frases motivacionales vacías, sino <strong className="text-[#6B4A2B]">principios que Jesús enseñaría hoy a empresarios, emprendedores y líderes</strong> que quieren hacer bien las cosas.
              </p>
              
              <div className="space-y-4">
                <p className="text-[#6B4A2B] font-semibold text-lg mb-4">Este libro es para ti si:</p>
                {[
                  'Nunca recibiste formación empresarial formal',
                  'Emprendiste por llamado, necesidad o pasión',
                  'Sientes culpa, confusión o desorden en los negocios',
                  'Quieres integrar fe, planificación y responsabilidad',
                  'Deseas dejar un legado más allá del dinero'
                ].map((item, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-[#C9A24D] rounded-full mt-2 flex-shrink-0"></div>
                    <p className="text-[#0F0F0F]/80 text-base">{item}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Aprenderás Section */}
      <section id="aprenderas" className="py-24 lg:py-32 bg-[#6B4A2B] relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.05]" style={{
          backgroundImage: 'linear-gradient(30deg, #0F0F0F 12%, transparent 12.5%, transparent 87%, #0F0F0F 87.5%, #0F0F0F), linear-gradient(150deg, #0F0F0F 12%, transparent 12.5%, transparent 87%, #0F0F0F 87.5%, #0F0F0F), linear-gradient(30deg, #0F0F0F 12%, transparent 12.5%, transparent 87%, #0F0F0F 87.5%, #0F0F0F), linear-gradient(150deg, #0F0F0F 12%, transparent 12.5%, transparent 87%, #0F0F0F 87.5%, #0F0F0F)',
          backgroundSize: '80px 140px',
          backgroundPosition: '0 0, 0 0, 40px 70px, 40px 70px'
        }}></div>
        
        <div className="max-w-7xl mx-auto px-6 lg:px-20 relative z-10">
          <div className="text-center mb-16">
            <h2 className="font-sans text-5xl lg:text-6xl font-bold text-[#C9A24D] mb-4">
              ¿QUÉ APRENDERÁS?
            </h2>
            <p className="font-serif italic text-white/70 text-xl">
              A través de capítulos claros y ejercicios prácticos
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: 'ri-compass-3-line',
                title: 'Ordenar tus sueños',
                description: 'Evaluar recursos reales y establecer metas alcanzables con sabiduría'
              },
              {
                icon: 'ri-shield-check-line',
                title: 'Liderar con integridad',
                description: 'Desarrollar carácter cristiano en cada decisión empresarial'
              },
              {
                icon: 'ri-scales-3-line',
                title: 'Decisiones financieras',
                description: 'Tomar decisiones con paz, sabiduría y responsabilidad bíblica'
              },
              {
                icon: 'ri-lightbulb-flash-line',
                title: 'Tecnología e innovación',
                description: 'Usar herramientas modernas con discernimiento y propósito'
              },
              {
                icon: 'ri-heart-3-line',
                title: 'Campo misionero',
                description: 'Ver tu negocio como plataforma para impactar vidas'
              },
              {
                icon: 'ri-plant-line',
                title: 'Legado generacional',
                description: 'Construir impacto social que trascienda tu tiempo'
              }
            ].map((item, index) => (
              <div 
                key={index}
                className="bg-white/15 backdrop-blur-sm border border-[#C9A24D]/30 rounded-xl p-8 hover:bg-white/20 transition-all hover:scale-105 hover:shadow-2xl"
              >
                <div className="w-12 h-12 flex items-center justify-center mb-6">
                  <i className={`${item.icon} text-[#C9A24D] text-4xl`}></i>
                </div>
                <h3 className="font-sans font-bold text-white text-xl mb-3 leading-tight">
                  {item.title}
                </h3>
                <p className="font-serif text-white/85 text-base leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <p className="text-white/90 text-lg font-serif italic">
              Todo con un lenguaje sencillo, accesible y profundamente bíblico.
            </p>
          </div>
        </div>
      </section>

      {/* Formatos Disponibles Section */}
      <section id="formatos" className="py-24 lg:py-32 bg-[#F7F5F2] relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.02]" style={{
          backgroundImage: 'repeating-linear-gradient(45deg, #6B4A2B 0, #6B4A2B 1px, transparent 0, transparent 50%)',
          backgroundSize: '10px 10px'
        }}></div>
        
        <div className="max-w-7xl mx-auto px-6 lg:px-20 relative z-10">
          <div className="text-center mb-16">
            <h2 className="font-serif text-5xl lg:text-6xl font-bold text-[#0F0F0F] mb-4">
              Formatos Disponibles
            </h2>
            <p className="text-[#6B4A2B]/70 text-xl">
              Elige el formato que mejor se adapte a tu estilo de aprendizaje
            </p>
          </div>
          
          <div className="grid lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {/* Libro Físico */}
            <div className="bg-gradient-to-br from-white to-[#F7F5F2] p-10 lg:p-12 relative group hover:shadow-2xl transition-all rounded-xl">
              <div className="absolute top-6 right-6 bg-[#C9A24D] text-[#0F0F0F] px-4 py-1.5 rounded-full text-xs font-bold tracking-wide">
                BESTSELLER
              </div>
              
              <div className="mb-8">
                <img 
                  src="https://static.readdy.ai/image/1a62d479ea6bd5856073579187b5ee28/1fd74645de097fe90da6ea21f9327ea7.png"
                  alt="Libro Físico + Cuaderno"
                  className="w-full max-w-sm mx-auto transform group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              
              <div className="flex items-center space-x-3 mb-4">
                <i className="ri-book-2-fill text-[#6B4A2B] text-3xl"></i>
                <h3 className="font-sans font-bold text-[#6B4A2B] text-2xl lg:text-3xl">
                  Libro Físico
                </h3>
              </div>
              
              <p className="font-serif text-[#0F0F0F]/70 text-base mb-6 leading-relaxed">
                Ideal para quienes desean subrayar, escribir y reflexionar con calma. Perfecto para clases, discipulado, mentorías y uso personal.
              </p>
              
              <div className="space-y-3 mb-8">
                {[
                  'Libro impreso de alta calidad',
                  'Perfecto para subrayar y anotar',
                  'Envío disponible en Amazon',
                  'Disponible en múltiples países'
                ].map((feature, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <i className="ri-checkbox-circle-fill text-[#C9A24D] text-lg"></i>
                    <span className="text-[#0F0F0F]/80 text-sm">{feature}</span>
                  </div>
                ))}
              </div>
              
              <div className="space-y-3">
                <p className="text-[#6B4A2B] font-semibold text-sm mb-3">Disponible en Amazon:</p>
                {[
                  { country: 'USA', flag: '🇺🇸', url: 'https://www.amazon.com/dp/B0GDXKW4H7' },
                  { country: 'España', flag: '🇪🇸', url: 'https://www.amazon.es/dp/B0GDXKW4H7' },
                  { country: 'Italia', flag: '🇮🇹', url: 'https://www.amazon.it/dp/B0GDXKW4H7' },
                  { country: 'Canadá', flag: '🇨🇦', url: 'https://www.amazon.ca/dp/B0GDXKW4H7' }
                ].map((item, index) => (
                  <a 
                    key={index}
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between w-full bg-[#0F0F0F] text-white px-5 py-3 rounded-lg font-medium hover:bg-[#2F2F2F] transition-all hover:scale-105 group/link whitespace-nowrap"
                  >
                    <div className="flex items-center space-x-3">
                      <span className="text-2xl">{item.flag}</span>
                      <span className="text-sm">{item.country}</span>
                    </div>
                    <i className="ri-amazon-fill text-xl group-hover/link:translate-x-1 transition-transform"></i>
                  </a>
                ))}
              </div>
            </div>
            
            {/* Cuaderno Físico */}
            <div className="bg-gradient-to-br from-white to-[#E8E6E3] p-10 lg:p-12 relative group hover:shadow-2xl transition-all rounded-xl">
              <div className="absolute top-6 right-6 bg-[#6B4A2B] text-[#C9A24D] px-4 py-1.5 rounded-full text-xs font-bold tracking-wide">
                COMPLEMENTO PERFECTO
              </div>
              
              <div className="mb-8">
                <img 
                  src="https://static.readdy.ai/image/1a62d479ea6bd5856073579187b5ee28/525a0179dd94022edcbe297b436c45d8.png"
                  alt="Cuaderno Físico"
                  className="w-full max-w-sm mx-auto transform group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              
              <div className="flex items-center space-x-3 mb-4">
                <i className="ri-book-open-line text-[#6B4A2B] text-3xl"></i>
                <h3 className="font-sans font-bold text-[#6B4A2B] text-2xl lg:text-3xl">
                  Cuaderno Físico
                </h3>
              </div>
              
              <p className="font-serif text-[#0F0F0F]/70 text-base mb-6 leading-relaxed">
                El complemento perfecto para tu libro. Espacio dedicado para reflexiones, ejercicios prácticos y aplicación directa a tu negocio.
              </p>
              
              <div className="space-y-3 mb-8">
                {[
                  'Cuaderno de trabajo impreso',
                  'Ejercicios prácticos guiados',
                  'Espacio para reflexiones personales',
                  'Aplicación directa al negocio'
                ].map((feature, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <i className="ri-checkbox-circle-fill text-[#C9A24D] text-lg"></i>
                    <span className="text-[#0F0F0F]/80 text-sm">{feature}</span>
                  </div>
                ))}
              </div>
              
              <div className="space-y-3">
                <p className="text-[#6B4A2B] font-semibold text-sm mb-3">Disponible en Amazon:</p>
                {[
                  { country: 'USA', flag: '🇺🇸', url: 'https://www.amazon.com/dp/B0GG4ZHBGP' },
                  { country: 'España', flag: '🇪🇸', url: 'https://www.amazon.es/dp/B0GG4ZHBGP' },
                  { country: 'Italia', flag: '🇮🇹', url: 'https://www.amazon.it/dp/B0GG4ZHBGP' },
                  { country: 'Canadá', flag: '🇨🇦', url: 'https://www.amazon.ca/dp/B0GG4ZHBGP' }
                ].map((item, index) => (
                  <a 
                    key={index}
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between w-full bg-[#C9A24D] text-[#0F0F0F] px-5 py-3 rounded-lg font-medium hover:bg-[#B89240] transition-all hover:scale-105 group/link whitespace-nowrap"
                  >
                    <div className="flex items-center space-x-3">
                      <span className="text-2xl">{item.flag}</span>
                      <span className="text-sm">{item.country}</span>
                    </div>
                    <i className="ri-amazon-fill text-xl group-hover/link:translate-x-1 transition-transform"></i>
                  </a>
                ))}
              </div>
            </div>

            {/* Versión Digital - Combo */}
            <div className="bg-gradient-to-br from-[#C9A24D]/10 to-white p-10 lg:p-12 relative group hover:shadow-2xl transition-all rounded-xl border-2 border-[#C9A24D]">
              <div className="absolute top-6 right-6 bg-[#25D366] text-white px-4 py-1.5 rounded-full text-xs font-bold tracking-wide">
                OFERTA ESPECIAL
              </div>
              
              <div className="mb-8">
                <img 
                  src="https://static.readdy.ai/image/1a62d479ea6bd5856073579187b5ee28/ff0a53e58a685753069b7ee1be34d5aa.png"
                  alt="Versión Digital Combo"
                  className="w-full max-w-sm mx-auto transform group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              
              <div className="flex items-center space-x-3 mb-4">
                <i className="ri-download-cloud-fill text-[#6B4A2B] text-3xl"></i>
                <h3 className="font-sans font-bold text-[#6B4A2B] text-2xl lg:text-3xl">
                  Versión Digital
                </h3>
              </div>
              
              <div className="bg-[#C9A24D]/20 border-l-4 border-[#C9A24D] p-4 rounded-r-lg mb-6">
                <p className="font-serif font-bold text-[#6B4A2B] text-lg">
                  🎁 Libro + Cuaderno GRATIS
                </p>
              </div>
              
              <p className="font-serif text-[#0F0F0F]/70 text-base mb-6 leading-relaxed">
                La opción práctica para estudiar desde cualquier lugar. Acceso inmediato al libro digital y cuaderno de trabajo incluido sin costo adicional.
              </p>
              
              <div className="space-y-3 mb-8">
                {[
                  'Acceso inmediato al contenido',
                  'Libro digital en formato PDF',
                  'Cuaderno de trabajo GRATIS',
                  'Estudia desde cualquier dispositivo'
                ].map((feature, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <i className="ri-checkbox-circle-fill text-[#C9A24D] text-lg"></i>
                    <span className="text-[#0F0F0F]/80 text-sm">{feature}</span>
                  </div>
                ))}
              </div>
              
              <a 
                href="https://coachedgardo.com/shop/EmprendedorDios"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center w-full bg-[#C9A24D] text-[#0F0F0F] px-6 py-4 rounded-lg font-bold text-base hover:bg-[#B89240] transition-all hover:scale-105 group/link whitespace-nowrap shadow-lg"
              >
                <i className="ri-shopping-cart-fill text-xl mr-3"></i>
                <span>OBTENER COMBO DIGITAL</span>
                <i className="ri-arrow-right-line text-xl ml-3 group-hover/link:translate-x-1 transition-transform"></i>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* El Cuaderno Section */}
      <section className="py-24 lg:py-32 bg-[#0F0F0F] relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'linear-gradient(rgba(107, 74, 43, 0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(107, 74, 43, 0.5) 1px, transparent 1px)',
          backgroundSize: '50px 50px'
        }}></div>
        
        <div className="max-w-7xl mx-auto px-6 lg:px-20 relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="font-sans font-bold text-[#C9A24D] text-4xl lg:text-5xl mb-6">
                EL CUADERNO DE TRABAJO
              </h2>
              
              <p className="font-serif italic text-white/80 text-xl lg:text-2xl mb-8 leading-relaxed">
                Este no es un cuaderno escolar.<br />
                Es un espacio personal para pensar, escribir, orar y tomar decisiones con honestidad delante de Dios.
              </p>
              
              <p className="font-serif text-white/90 text-base lg:text-lg mb-10 leading-relaxed">
                Cada capítulo incluye reflexiones guiadas, ejercicios prácticos, evaluaciones sencillas y aplicación directa al negocio. No busca respuestas correctas, sino respuestas verdaderas.
              </p>
              
              <div className="space-y-5 mb-10">
                {[
                  { icon: 'ri-edit-line', text: 'Reflexiones guiadas para profundizar' },
                  { icon: 'ri-settings-3-line', text: 'Ejercicios prácticos aplicables' },
                  { icon: 'ri-checkbox-multiple-line', text: 'Evaluaciones sencillas y honestas' },
                  { icon: 'ri-arrow-right-up-line', text: 'Aplicación directa a tu negocio' }
                ].map((item, index) => (
                  <div key={index} className="flex items-center space-x-4">
                    <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
                      <i className={`${item.icon} text-[#C9A24D] text-2xl`}></i>
                    </div>
                    <span className="text-white text-base">{item.text}</span>
                  </div>
                ))}
              </div>
              
              <div className="bg-[#C9A24D]/10 border-l-4 border-[#C9A24D] p-6 rounded-r-lg">
                <p className="font-serif font-bold italic text-[#C9A24D] text-lg leading-relaxed">
                  "No busca respuestas correctas, sino respuestas verdaderas."
                </p>
              </div>
            </div>
            
            <div className="relative">
              <img 
                src="https://public.readdy.ai/ai/img_res/b5b552635f053cc7913fa6b60827c7e2.jpg"
                alt="Cuaderno de Trabajo"
                className="w-full rounded-xl shadow-2xl"
              />
              <div className="absolute -top-8 -left-8 w-40 h-40 bg-[#C9A24D]/20 rounded-full blur-3xl"></div>
              <div className="absolute -bottom-8 -right-8 w-40 h-40 bg-[#C9A24D]/20 rounded-full blur-3xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Sobre el Autor Section */}
      <section className="py-24 lg:py-32 bg-[#F7F5F2] relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%236B4A2B" fill-opacity="1"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'
        }}></div>
        
        <div className="max-w-7xl mx-auto px-6 lg:px-20 relative z-10">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center space-x-4 mb-6">
              <div className="w-12 h-0.5 bg-[#C9A24D]"></div>
              <span className="text-[#6B4A2B] text-sm font-bold tracking-widest uppercase">Sobre el Autor</span>
              <div className="w-12 h-0.5 bg-[#C9A24D]"></div>
            </div>
            <h2 className="font-serif text-4xl lg:text-5xl font-bold text-[#0F0F0F] mb-4">
              Edgardo Moreno Viera
            </h2>
            <p className="font-serif italic text-[#C9A24D] text-xl lg:text-2xl">
              Emprendedor, Pastor y Comunicador
            </p>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative order-2 lg:order-1">
              <div className="relative rounded-xl overflow-hidden shadow-2xl">
                <img 
                  src="https://static.readdy.ai/image/1a62d479ea6bd5856073579187b5ee28/71f02634b51faadbe849a1f0597c60c2.png"
                  alt="Edgardo Moreno Viera"
                  className="w-full h-auto"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-[#C9A24D]/10 rounded-full blur-3xl"></div>
            </div>
            
            <div className="order-1 lg:order-2">
              <p className="text-[#0F0F0F]/80 text-base lg:text-lg mb-6 leading-relaxed">
                Edgardo Moreno Viera es <strong className="text-[#6B4A2B]">emprendedor por herencia y por convicción</strong>. Desde los 12 años aprendió el poder de los negocios y de la multiplicación gracias a la influencia de su abuelita Carmen, de su padre Baltazar Moreno y de su madre Gloria Viera de Moreno, todos emprendedores por vocación y por necesidad.
              </p>
              
              <p className="text-[#0F0F0F]/80 text-base lg:text-lg mb-6 leading-relaxed">
                A lo largo de su vida ha combinado el mundo de los negocios con su carrera como comunicador social y, desde hace más de 30 años, con el poderoso universo digital.
              </p>
              
              <div className="bg-[#C9A24D]/10 border-l-4 border-[#C9A24D] p-6 rounded-r-lg mb-6">
                <h3 className="font-sans font-bold text-[#6B4A2B] text-lg mb-3">Formación Académica</h3>
                <p className="text-[#0F0F0F]/80 text-base leading-relaxed">
                  Estudió <strong>Ciencias de la Comunicación y Periodismo</strong>, con especializaciones en Planificación Estratégica de Negocios, Mercadeo Digital e Inteligencia Artificial aplicada al crecimiento empresarial.
                </p>
              </div>
              
              <p className="text-[#0F0F0F]/80 text-base lg:text-lg mb-6 leading-relaxed">
                A lo largo de su trayectoria ha desarrollado diversos emprendimientos y ha acompañado a miles de personas en la transición del mundo análogo al digital.
              </p>
              
              <div className="space-y-4 mb-6">
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-[#C9A24D] rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-[#0F0F0F]/80 text-base">
                    <strong className="text-[#6B4A2B]">Pastor Principal</strong> de Fortaleza Whittier en el Condado de Los Angeles, California
                  </p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-[#C9A24D] rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-[#0F0F0F]/80 text-base">
                    <strong className="text-[#6B4A2B]">Director de Extensión</strong> de World Mission University y American Evangelical University
                  </p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-[#C9A24D] rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-[#0F0F0F]/80 text-base">
                    <strong className="text-[#6B4A2B]">Consejero Certificado</strong> por Focus on the Family
                  </p>
                </div>
              </div>
              
              <p className="font-serif italic text-[#6B4A2B] text-lg leading-relaxed">
                Reside en California desde hace más de dos décadas, donde continúa sirviendo a la comunidad a través del ministerio pastoral y el acompañamiento empresarial.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Bono Webinar Section */}
      <section id="webinar" className="py-24 lg:py-32 relative overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://readdy.ai/api/search-image?query=Elegant%20wooden%20desk%20workspace%20with%20laptop%20computer%2C%20open%20bible%2C%20steaming%20coffee%20cup%2C%20natural%20window%20light%2C%20peaceful%20home%20office%20setting%2C%20warm%20morning%20ambiance%2C%20professional%20lifestyle%20photography%2C%20soft%20focus%20background%2C%20inspirational%20work%20environment%2C%20golden%20tones&width=1920&height=1080&seq=workspace-webinar-001&orientation=landscape"
            alt="Workspace"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-[#C9A24D]/70"></div>
        </div>
        
        <div className="max-w-4xl mx-auto px-6 relative z-10">
          <div className="bg-[#F7F5F2] rounded-2xl shadow-2xl p-10 lg:p-16 border-t-8 border-[#C9A24D]">
            <div className="text-center mb-8">
              <div className="inline-flex items-center space-x-2 bg-[#6B4A2B]/10 px-5 py-2 rounded-full mb-6">
                <i className="ri-video-line text-[#6B4A2B] text-lg"></i>
                <span className="text-[#6B4A2B] text-sm font-bold tracking-widest uppercase">Bono Exclusivo</span>
              </div>
              
              <h2 className="font-serif font-bold text-[#0F0F0F] text-4xl lg:text-5xl mb-4">
                Webinar Gratuito Exclusivo
              </h2>
              
              <p className="font-serif italic text-[#C9A24D] text-xl lg:text-2xl mb-8">
                "Emprender con Fe y Propósito en el Mundo Real"
              </p>
            </div>
            
            <div className="bg-white/50 rounded-xl p-8 mb-8">
              <div className="flex items-start space-x-4 mb-6">
                <div className="w-10 h-10 bg-[#C9A24D] rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold">1</span>
                </div>
                <div>
                  <p className="text-[#0F0F0F] text-lg">
                    <i className="ri-camera-line text-[#C9A24D] mr-2"></i>
                    <strong>Envía una foto</strong> con tu libro o cuaderno
                  </p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4">
                <div className="w-10 h-10 bg-[#C9A24D] rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold">2</span>
                </div>
                <div>
                  <p className="text-[#0F0F0F] text-lg">
                    <i className="ri-gift-line text-[#C9A24D] mr-2"></i>
                    <strong>Recibe invitación gratuita</strong> al webinar exclusivo
                  </p>
                </div>
              </div>
            </div>
            
            <div className="mb-8">
              <h3 className="font-sans font-bold text-[#6B4A2B] text-xl mb-4">En este webinar profundizamos en:</h3>
              <div className="grid md:grid-cols-2 gap-4">
                {[
                  'Errores comunes del emprendedor cristiano',
                  'Cómo aterrizar sueños sin perder fe',
                  'Orden, planificación y visión eterna',
                  'Preguntas y respuestas en vivo'
                ].map((topic, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <i className="ri-checkbox-circle-fill text-[#C9A24D] text-lg"></i>
                    <span className="text-[#0F0F0F]/80 text-base">{topic}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <a 
              href="https://bit.ly/EDDGrupo"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full bg-[#6B4A2B] text-white py-5 rounded-lg font-semibold text-lg hover:bg-[#5A3D23] transition-all hover:scale-105 flex items-center justify-center space-x-3 whitespace-nowrap"
            >
              <i className="ri-camera-line text-2xl"></i>
              <span>ENVIAR FOTO Y RECIBIR INVITACIÓN</span>
              <i className="ri-arrow-right-line text-xl"></i>
            </a>
          </div>
        </div>
      </section>

      {/* Comunidad Section */}
      <section id="comunidad" className="py-24 lg:py-32 bg-[#C9A24D]/20 relative overflow-hidden">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="w-20 h-20 flex items-center justify-center mx-auto mb-8">
            <i className="ri-whatsapp-line text-[#6B4A2B] text-7xl"></i>
          </div>
          
          <h2 className="font-serif font-bold text-[#6B4A2B] text-4xl lg:text-5xl mb-4">
            Únete a la Comunidad
          </h2>
          
          <p className="font-sans font-bold text-[#C9A24D] text-2xl lg:text-3xl mb-8">
            Emprendedor de Dios
          </p>
          
          <p className="font-serif text-[#0F0F0F]/70 text-lg mb-12 max-w-2xl mx-auto leading-relaxed">
            No camines solo. Únete a una comunidad de emprendedores cristianos que desean crecer con fe madura, orden y propósito.
          </p>
          
          <div className="grid md:grid-cols-2 gap-6 mb-12 max-w-3xl mx-auto">
            {[
              { icon: 'ri-message-3-line', text: 'Reflexiones semanales' },
              { icon: 'ri-notification-3-line', text: 'Recordatorios prácticos' },
              { icon: 'ri-calendar-event-line', text: 'Invitaciones a eventos' },
              { icon: 'ri-team-line', text: 'Comunidad y apoyo' }
            ].map((benefit, index) => (
              <div key={index} className="bg-white/50 backdrop-blur-sm rounded-xl p-6 hover:bg-white/70 transition-all">
                <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3">
                  <i className={`${benefit.icon} text-[#C9A24D] text-3xl`}></i>
                </div>
                <p className="text-[#0F0F0F] font-medium text-base">{benefit.text}</p>
              </div>
            ))}
          </div>
          
          <a 
            href="https://chat.whatsapp.com/JdwZjvw5Qx54ITX8HpEyWX"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center space-x-3 bg-[#25D366] text-white px-12 py-5 rounded-lg font-semibold text-lg hover:bg-[#20BA5A] transition-all hover:scale-105 shadow-xl whitespace-nowrap"
          >
            <i className="ri-whatsapp-fill text-2xl"></i>
            <span>UNIRME AL GRUPO DE WHATSAPP</span>
          </a>
        </div>
      </section>

      {/* Formulario de Preguntas Section */}
      <section id="contacto" className="py-24 lg:py-32 bg-[#F7F5F2]">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="font-serif font-bold text-[#0F0F0F] text-4xl lg:text-5xl mb-4">
              ¿Tienes Preguntas?
            </h2>
            <p className="text-[#6B4A2B]/70 text-lg">
              Estamos aquí para ayudarte. Envíanos tu consulta y te responderemos pronto.
            </p>
          </div>
          
          <div className="bg-white rounded-2xl shadow-xl p-8 lg:p-12 border-t-4 border-[#C9A24D]">
            <form className="space-y-6" data-readdy-form id="contact-form" onSubmit={handleFormSubmit}>
              <div>
                <label htmlFor="name" className="block text-[#6B4A2B] font-semibold mb-2 text-sm">
                  Nombre Completo *
                </label>
                <input 
                  type="text"
                  id="name"
                  name="name"
                  required
                  disabled={formStatus === 'submitting'}
                  className="w-full px-4 py-3 border-2 border-[#C9A24D]/30 rounded-lg focus:border-[#C9A24D] focus:outline-none transition-colors text-sm disabled:opacity-50"
                  placeholder="Tu nombre"
                />
              </div>
              
              <div>
                <label htmlFor="email" className="block text-[#6B4A2B] font-semibold mb-2 text-sm">
                  Correo Electrónico *
                </label>
                <input 
                  type="email"
                  id="email"
                  name="email"
                  required
                  disabled={formStatus === 'submitting'}
                  className="w-full px-4 py-3 border-2 border-[#C9A24D]/30 rounded-lg focus:border-[#C9A24D] focus:outline-none transition-colors text-sm disabled:opacity-50"
                  placeholder="tu@email.com"
                />
              </div>
              
              <div>
                <label htmlFor="subject" className="block text-[#6B4A2B] font-semibold mb-2 text-sm">
                  Asunto
                </label>
                <input 
                  type="text"
                  id="subject"
                  name="subject"
                  disabled={formStatus === 'submitting'}
                  className="w-full px-4 py-3 border-2 border-[#C9A24D]/30 rounded-lg focus:border-[#C9A24D] focus:outline-none transition-colors text-sm disabled:opacity-50"
                  placeholder="¿Sobre qué quieres preguntar?"
                />
              </div>
              
              <div>
                <label htmlFor="message" className="block text-[#6B4A2B] font-semibold mb-2 text-sm">
                  Mensaje *
                </label>
                <textarea 
                  id="message"
                  name="message"
                  required
                  rows={6}
                  maxLength={500}
                  disabled={formStatus === 'submitting'}
                  className="w-full px-4 py-3 border-2 border-[#C9A24D]/30 rounded-lg focus:border-[#C9A24D] focus:outline-none transition-colors resize-none text-sm disabled:opacity-50"
                  placeholder="Escribe tu pregunta o comentario aquí (máximo 500 caracteres)"
                ></textarea>
                <p className="text-xs text-[#6B4A2B]/60 mt-1">Máximo 500 caracteres</p>
              </div>

              {formStatus === 'success' && (
                <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded">
                  <p className="text-green-700 text-sm">
                    <i className="ri-checkbox-circle-fill mr-2"></i>
                    ¡Mensaje enviado con éxito! Te responderemos pronto.
                  </p>
                </div>
              )}

              {formStatus === 'error' && (
                <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded">
                  <p className="text-red-700 text-sm">
                    <i className="ri-error-warning-fill mr-2"></i>
                    Hubo un error al enviar el mensaje. Por favor, intenta de nuevo.
                  </p>
                </div>
              )}
              
              <button 
                type="submit"
                disabled={formStatus === 'submitting'}
                className="w-full bg-[#C9A24D] text-[#0F0F0F] py-4 rounded-lg font-semibold text-base hover:bg-[#B89240] transition-all hover:scale-105 flex items-center justify-center space-x-2 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
              >
                <span>{formStatus === 'submitting' ? 'ENVIANDO...' : 'ENVIAR MENSAJE'}</span>
                <i className="ri-send-plane-fill text-lg"></i>
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#6B4A2B] text-white">
        <div className="py-16 lg:py-20">
          <div className="max-w-7xl mx-auto px-6 lg:px-20">
            <div className="text-center mb-16">
              <p className="font-serif italic text-[#C9A24D] text-3xl lg:text-4xl mb-6 leading-relaxed">
                Jesús no separó la fe de la vida real.<br />
                Tampoco separó el Reino del trabajo.
              </p>
              <p className="text-white/85 text-lg leading-relaxed">
                <strong className="text-[#C9A24D]">Emprendedor de Dios</strong> no es solo un libro.<br />
                Es una invitación a construir con sabiduría lo que Dios puso en tus manos.
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-12 border-t border-[#C9A24D]/30 pt-12">
              <div>
                <div className="flex items-center space-x-3 mb-4">
                  <img 
                    src="https://static.readdy.ai/image/1a62d479ea6bd5856073579187b5ee28/40c1fc3e267b957eadf5ce9a3c5ee7f2.png" 
                    alt="Logo" 
                    className="h-10 w-auto"
                  />
                </div>
                <h3 className="font-serif text-xl font-bold text-[#C9A24D] mb-3">EMPRENDEDOR DE DIOS</h3>
                <p className="text-white/70 text-sm leading-relaxed mb-4">
                  Principios eternos para emprender con fe, orden y propósito.
                </p>
                <div className="flex items-center space-x-4">
                  <a href="#" className="text-[#C9A24D] hover:text-white transition-colors">
                    <i className="ri-facebook-circle-fill text-2xl"></i>
                  </a>
                  <a href="#" className="text-[#C9A24D] hover:text-white transition-colors">
                    <i className="ri-instagram-line text-2xl"></i>
                  </a>
                  <a href="#" className="text-[#C9A24D] hover:text-white transition-colors">
                    <i className="ri-youtube-fill text-2xl"></i>
                  </a>
                </div>
              </div>
              
              <div>
                <div className="border-b border-[#C9A24D] pb-2 mb-4">
                  <h4 className="font-sans font-bold text-[#C9A24D] text-xs tracking-widest uppercase">Navegación</h4>
                </div>
                <ul className="space-y-3">
                  {[
                    { label: 'Inicio', id: '' },
                    { label: 'Sobre el Libro', id: 'sobre' },
                    { label: 'Formatos', id: 'formatos' },
                    { label: 'Webinar', id: 'webinar' },
                    { label: 'Comunidad', id: 'comunidad' },
                    { label: 'Contacto', id: 'contacto' }
                  ].map((link, index) => (
                    <li key={index}>
                      <button 
                        onClick={() => link.id ? scrollToSection(link.id) : window.scrollTo({ top: 0, behavior: 'smooth' })}
                        className="text-white/80 hover:text-[#C9A24D] transition-colors text-sm hover:underline"
                      >
                        {link.label}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
              
              <div>
                <div className="border-b border-[#C9A24D] pb-2 mb-4">
                  <h4 className="font-sans font-bold text-[#C9A24D] text-xs tracking-widest uppercase">Contacto</h4>
                </div>
                <div className="space-y-3 mb-6">
                  <a href="mailto:info@emprendedordedios.com" className="flex items-center space-x-2 text-white/80 hover:text-[#C9A24D] transition-colors text-sm">
                    <i className="ri-mail-line"></i>
                    <span>info@emprendedordedios.com</span>
                  </a>
                </div>
                <div>
                  <p className="text-white/70 text-xs mb-3">Suscríbete a nuestro boletín</p>
                  <div className="flex space-x-2">
                    <input 
                      type="email"
                      placeholder="Tu email"
                      className="flex-1 px-3 py-2 bg-transparent border border-[#C9A24D] rounded text-white placeholder-white/50 focus:outline-none focus:border-white text-sm"
                    />
                    <button className="bg-[#C9A24D] text-[#0F0F0F] px-4 py-2 rounded font-semibold hover:bg-[#B89240] transition-colors text-sm whitespace-nowrap">
                      Suscribir
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="bg-[#0F0F0F] py-6">
          <div className="max-w-7xl mx-auto px-6 lg:px-20">
            <div className="flex flex-col md:flex-row items-center justify-between space-y-3 md:space-y-0 text-sm">
              <p className="text-gray-400">© 2025 Emprendedor de Dios. Todos los derechos reservados.</p>
              <p className="font-serif italic text-[#C9A24D]/60 text-xs">Hecho con fe y propósito</p>
              <div className="flex items-center space-x-4">
                <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#C9A24D] transition-colors">
                  Website Builder
                </a>
                <span className="text-gray-600">|</span>
                <a href="#" className="text-gray-400 hover:text-[#C9A24D] transition-colors">Privacidad</a>
                <span className="text-gray-600">|</span>
                <a href="#" className="text-gray-400 hover:text-[#C9A24D] transition-colors">Términos</a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
